import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { TreeTableLazyLoadEvent } from 'primeng/treetable';
import { AbstractCrudMandantService } from '@service/abstract-crud-mandant.service';
import { ContextService } from '@service/context.service';
import { TableService } from '@shared/table/table-service';
import { AnyValue } from '@shared/table/table.types';

export abstract class TreeTableCrudService<T> extends TableService<TreeNode> {
  private readonly contextService = inject(ContextService);

  protected abstract readonly entityService: AbstractCrudMandantService<T>;

  protected abstract toTreeNode(entity: T): TreeNode;

  protected abstract toEntity(node: TreeNode): T;

  protected abstract getId(entity: T): number | string;

  getData(
    focusDateOrYear?: Date | number,
    treeTableLazyLoadEvent?: TreeTableLazyLoadEvent
  ): Observable<{ entities: TreeNode[]; total: number }> {
    const focusDate = focusDateOrYear instanceof Date ? focusDateOrYear : undefined;
    return this.entityService.list(this.contextService.getMandantIdInstant(), focusDate, treeTableLazyLoadEvent).pipe(
      map((response) => ({
        entities: response.entities.map((entity) => this.toTreeNode(entity)),
        total: response.total,
      }))
    );
  }

  create(e: TreeNode): Observable<TreeNode> {
    return this.entityService
      .create(this.contextService.getMandantIdInstant(), this.toEntity(e))
      .pipe(map((entity) => this.toTreeNode(entity)));
  }

  remove(e: TreeNode): Observable<AnyValue> {
    const entity = this.toEntity(e);
    return this.entityService.delete(this.contextService.getMandantIdInstant(), this.getId(entity));
  }

  update(e: TreeNode): Observable<TreeNode> {
    const entity = this.toEntity(e);
    return this.entityService
      .update(this.contextService.getMandantIdInstant(), this.getId(entity), entity)
      .pipe(map((updated) => this.toTreeNode(updated)));
  }
}
